import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { Button } from 'primeng/button';
import { RelatorioContainer } from '@/shared/components/relatorios/components/relatorio-container/relatorio-container';
import { RelatorioFormBase } from '@/shared/components/relatorios/relatorio-form.base';
import { RelatorioJobFacade } from '@/shared/components/relatorios/relatorio-job-base';
import { CertidaoDiretorClinicoPjService } from '@/core/api/sicom-api/registro/prestador/relatorios/certidao-diretor-clinico-pj/certidao-diretor-clinico-pj.service';
import { ReportCertidaoDiretorClinicoPjRequest, ReportCertidaoDiretorClinicoPjResponse } from '@/core/api/sicom-api/registro/prestador/relatorios/certidao-diretor-clinico-pj/certidao-diretor-clinico-pj.model';
import { CertidaoDiretorClinicoPjJobService } from '@/core/services/certidao-diretor-clinico-pj-job.service';
import { PessoaService } from '@/shared/services/pessoa.service';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

type Req = ReportCertidaoDiretorClinicoPjRequest;
type Resp = ReportCertidaoDiretorClinicoPjResponse;

@Component({
    selector: 'app-certidao-diretor-clinico-pj',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AsyncPipe, Button, RelatorioContainer, CanDirective],
    templateUrl: './certidao-diretor-clinico-pj.html',
    styleUrl: './certidao-diretor-clinico-pj.scss'
})
/**
 * Relatório PDF assíncrono: Certidão de Diretor Clínico PJ (variante base "direção clínica").
 * Herda `RelatorioFormBase`: export/polling/download/erro vêm da base; aqui só resolvemos a
 * identificação do prestador (nuCrm/sgUf via `PessoaService`), sem filtros adicionais.
 */
export class ReportCertidaoDiretorClinicoPjComponent extends RelatorioFormBase<Req, Resp> {
    /** Gate do botao Gerar: o backend exige esta role em /reports/** do modulo. */
    protected override readonly roleGerar = PERMISSOES.prestadores.relatorios.gerar;

    private readonly certidaoService = inject(CertidaoDiretorClinicoPjService);
    private readonly pessoaService = inject(PessoaService);
    readonly certidaoJobService = inject(CertidaoDiretorClinicoPjJobService);

    /** Stream do job para o `app-relatorio-container` (preview/PDF) no template. */
    readonly job$ = this.certidaoJobService.job$;

    protected get jobFacade(): RelatorioJobFacade {
        return this.certidaoJobService;
    }

    /** Monta o request a partir do prestador selecionado; retorna null (e avisa) se faltar CRM/UF. */
    protected buildRequest(): Req | null {
        const pessoa = this.pessoaService.getPessoa();
        const pessoaReference = this.pessoaService.getPessoaReference();
        const nuCrm = Number(pessoa?.dadosGerais?.crm || pessoa?.crm || pessoa?.registro || 0);
        const sgUf = pessoa?.uf || pessoaReference?.uf || '';

        if (!nuCrm || !sgUf) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: 'Selecione um prestador antes de gerar a certidão.'
            });
            return null;
        }

        return { nuCrm, sgUf };
    }

    protected exportar(request: Req) {
        return this.certidaoService.exportarPdf(request);
    }

    limparFormulario(): void {
        this.limparJob();
    }
}
